// Shared speciality derivation logic.
// Used by specialistFiltering.ts (runtime) and scripts/build-doctors-index.ts (build).
// No framework dependencies — pure string operations only.

export interface Speciality {
  slug: string;
  title: string;
}

export interface SpecialistDoctor {
  slug?: string | null;
  type?: string | null;
  fullName?: string | null;
  heading?: string | null;
  description?: string | null;
  primarySpeciality?: string | Speciality | null;
  specialities?: string[] | Speciality[] | null;
  gender?: string | { title?: string | null; slug?: string | null } | null;
  typeOfDoctor?: Speciality[] | null;
}

export const cleanSpeciality = (value: unknown): Speciality | null => {
  if (!value) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed
      ? { slug: trimmed.toLowerCase().replace(/\s+/g, '-'), title: trimmed }
      : null;
  }

  if (typeof value !== 'object') return null;

  const speciality = value as Partial<Speciality>;
  const slug = (speciality.slug ?? '').trim();
  const title = (speciality.title ?? '').trim();

  if (!slug || !title) return null;

  return { slug, title };
};

export const sortSpecialitiesByTitle = (a: Speciality, b: Speciality) =>
  a.title.localeCompare(b.title, 'en', { sensitivity: 'base' });

export const getDoctorMainSpeciality = (
  doctor: SpecialistDoctor,
): Speciality | null => {
  const primarySpeciality = doctor.primarySpeciality;

  if (typeof primarySpeciality === 'string') {
    const trimmed = primarySpeciality.trim();
    return trimmed
      ? { slug: trimmed.toLowerCase().replace(/\s+/g, '-'), title: trimmed }
      : null;
  }

  const cleaned = cleanSpeciality(primarySpeciality);
  if (cleaned) return cleaned;

  return (
    doctor.specialities
      ?.map(cleanSpeciality)
      .find((speciality): speciality is Speciality => Boolean(speciality)) ??
    null
  );
};

export const getDoctorSubSpecialities = (
  doctor: SpecialistDoctor,
  mainSpeciality: Speciality,
): Speciality[] => {
  return (
    doctor.specialities
      ?.map(cleanSpeciality)
      .filter((speciality): speciality is Speciality => Boolean(speciality))
      .filter((speciality) => speciality.slug !== mainSpeciality.slug) ?? []
  );
};
